import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye, FaPencilAlt, FaMinusCircle } from 'react-icons/fa';

const PainCharts = () => {
        const [searchTerm, setSearchTerm] = useState('');
        const [selectedCharts, setSelectedCharts] = useState([]);
        const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
        const [activeTab, setActiveTab] = useState('All');

        const tabs = [
                { id: 'All', label: 'All' },
                { id: 'Ongoing', label: 'Ongoing' },
                { id: 'Completed', label: 'Completed' },
        ];

        const [charts] = useState([
                {
                        id: 1,
                        procedureDate: '30/11/2024',
                        procedure: 'Right C4/5/6 RFN & Caudal Epidural',
                        patientName: 'Mr Christian Bannard',
                        currentPain: 4,
                        previousPain: 7,
                        lastUpdated: '2024-11-24 8:00 a.m.',
                        status: 'Ongoing',
                },
                // Add more example data as needed
        ]);

        const handleSelectAll = (e) => {
                if (e.target.checked) {
                        setSelectedCharts(charts.map(chart => chart.id));
                } else {
                        setSelectedCharts([]);
                }
        };

        const handleSelectOne = (chartId) => {
                setSelectedCharts(prev => {
                        if (prev.includes(chartId)) {
                                return prev.filter(id => id !== chartId);
                        } else {
                                return [...prev, chartId];
                        }
                });
        };

        const requestSort = (key) => {
                let direction = 'ascending';
                if (sortConfig.key === key && sortConfig.direction === 'ascending') {
                        direction = 'descending';
                }
                setSortConfig({ key, direction });
        };

        const getSortedCharts = () => {
                let filteredCharts = [...charts];

                if (activeTab !== 'All') {
                        filteredCharts = filteredCharts.filter(chart => chart.status === activeTab);
                }

                if (searchTerm) {
                        filteredCharts = filteredCharts.filter(chart =>
                                Object.values(chart).some(value =>
                                        String(value).toLowerCase().includes(searchTerm.toLowerCase())
                                )
                        );
                }

                if (sortConfig.key) {
                        filteredCharts.sort((a, b) => {
                                if (a[sortConfig.key] < b[sortConfig.key]) {
                                        return sortConfig.direction === 'ascending' ? -1 : 1;
                                }
                                if (a[sortConfig.key] > b[sortConfig.key]) {
                                        return sortConfig.direction === 'ascending' ? 1 : -1;
                                }
                                return 0;
                        });
                }

                return filteredCharts;
        };

        return (
                <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                                <h1 className="text-2xl font-semibold text-base-content">Pain Charts</h1>
                        </div>

                        <div className="mb-6 flex items-center space-x-4">
                                <div className="join">
                                        <div className="relative join-item">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                        <svg className="w-4 h-4 text-base-content/60" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                                                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                                                        </svg>
                                                </div>
                                                <input
                                                        type="text"
                                                        placeholder="Search charts..."
                                                        className="input input-bordered w-64 pl-10 join-item"
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                        </div>
                                        <button className="btn btn-primary join-item">Search</button>
                                </div>
                        </div>

                        <div className="tabs tabs-bordered mb-6">
                                {tabs.map((tab) => (
                                        <button
                                                key={tab.id}
                                                className={`tab tab-lg ${activeTab === tab.id ? 'tab-active' : ''}`}
                                                onClick={() => setActiveTab(tab.id)}
                                        >
                                                {tab.label}
                                                {tab.id !== 'All' && (
                                                        <span className="ml-2 badge badge-sm">
                                                                {charts.filter(p => p.status === tab.id).length}
                                                        </span>
                                                )}
                                        </button>
                                ))}
                        </div>

                        <div className="relative overflow-x-auto rounded-lg">
                                <table className="table table-zebra bg-base-100/50">
                                        <thead>
                                                <tr>
                                                        <th className="w-4">
                                                                <input
                                                                        type="checkbox"
                                                                        className="checkbox checkbox-sm"
                                                                        onChange={handleSelectAll}
                                                                        checked={selectedCharts.length === charts.length}
                                                                />
                                                        </th>
                                                        <th>Procedure Date</th>
                                                        <th>Procedure</th>
                                                        <th>Patient Name</th>
                                                        <th>Current Pain</th>
                                                        <th>Previous Pain</th>
                                                        <th>Last Updated</th>
                                                        <th>Status</th>
                                                        <th>Commands</th>
                                                </tr>
                                        </thead>
                                        <tbody>
                                                {getSortedCharts().map((chart) => (
                                                        <tr key={chart.id}>
                                                                <td>
                                                                        <input
                                                                                type="checkbox"
                                                                                className="checkbox checkbox-sm"
                                                                                checked={selectedCharts.includes(chart.id)}
                                                                                onChange={() => handleSelectOne(chart.id)}
                                                                        />
                                                                </td>
                                                                <td>{chart.procedureDate}</td>
                                                                <td>
                                                                        <Link
                                                                                to={`/pain-charts/${chart.id}`}
                                                                                className="hover:underline cursor-pointer font-medium"
                                                                        >
                                                                                {chart.procedure}
                                                                        </Link>
                                                                </td>
                                                                <td>{chart.patientName}</td>
                                                                <td>{chart.currentPain}</td>
                                                                <td>{chart.previousPain}</td>
                                                                <td>{chart.lastUpdated}</td>
                                                                <td>{chart.status}</td>
                                                                <td>
                                                                        <div className="flex gap-2">
                                                                                <Link to={`/pain-charts/${chart.id}`} className="btn btn-ghost btn-xs">
                                                                                        <FaEye className="w-4 h-4" />
                                                                                </Link>
                                                                                <Link to={`/pain-charts/${chart.id}/edit`} className="btn btn-ghost btn-xs">
                                                                                        <FaPencilAlt className="w-4 h-4" />
                                                                                </Link>
                                                                                <button
                                                                                        className="btn btn-ghost btn-xs"
                                                                                        onClick={() => {/* Handle mark inactive */ }}
                                                                                >
                                                                                        <FaMinusCircle className="w-4 h-4" />
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                ))}
                                        </tbody>
                                </table>
                        </div>
                </div>
        );
};

export default PainCharts;
